import { useState, useEffect } from "react";
import { useAppSelector, useAppDispatch } from "hooks/reduxHooks";
import Styled from "./TeamRecommendations.style";
import { selectCurrentClinician } from "../../../../../store/ClinicianStore/selectors";
import { setCurrentClinician } from "../../../../../store/ClinicianStore/clinicianSlice";
import { updateClinicianRecommendations } from "../../../../../repositories/coachApi";
import { CircularProgress, Button } from "@mui/material";
import { btnStyle } from "../../../../auth/styles";
import { enqueueSnackbar } from "notistack";

const TeamRecommendations = () => {
  const [normal, setNormal] = useState("");
  const [significant, setSignificant] = useState("");
  const [severe, setSevere] = useState("");
  const [normalLink, setNormalLink] = useState("");
  const [significantLink, setSignificantLink] = useState("");
  const [severeLink, setSevereLink] = useState("");
  const [normalLinkText, setNormalLinkText] = useState("Watch video");
  const [significantLinkText, setSignificantLinkText] = useState("Watch video");
  const [severeLinkText, setSevereLinkText] = useState("Watch video");
  const [loading, setLoading] = useState(false);
  const currentClinician = useAppSelector(selectCurrentClinician);
  const dispatch = useAppDispatch();

  const handleUrlChange =
    (setter: (value: string) => void) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      if (
        value &&
        !value.startsWith("http://") &&
        !value.startsWith("https://")
      ) {
        setter(`https://${value}`);
      } else {
        setter(value);
      }
    };

  const handleSave = async () => {
    if (!currentClinician) return;
    setLoading(true);
    try {
      await updateClinicianRecommendations(currentClinician._id, {
        recommendations: {
          normal,
          significant,
          severe,
          normalLink,
          significantLink,
          severeLink,
          normalLinkText,
          significantLinkText,
          severeLinkText,
        },
      });
      dispatch(
        setCurrentClinician({
          ...currentClinician,
          recommendations: {
            normal,
            significant,
            severe,
            normalLink,
            significantLink,
            severeLink,
            normalLinkText,
            significantLinkText,
            severeLinkText,
          },
        })
      );
      enqueueSnackbar("Recommendations updated successfully", {
        variant: "success",
      });
    } catch (error) {
      enqueueSnackbar("Error updating recommendations", {
        variant: "error",
      });
    }
    setLoading(false);
  };

  useEffect(() => {
    if (currentClinician) {
      setNormal(currentClinician?.recommendations?.normal || "");
      setSignificant(currentClinician?.recommendations?.significant || "");
      setSevere(currentClinician?.recommendations?.severe || "");
      setNormalLink(currentClinician?.recommendations?.normalLink || "");
      setSignificantLink(
        currentClinician?.recommendations?.significantLink || ""
      );
      setSevereLink(currentClinician?.recommendations?.severeLink || "");
      setNormalLinkText(
        currentClinician?.recommendations?.normalLinkText || "Watch video"
      );
      setSignificantLinkText(
        currentClinician?.recommendations?.significantLinkText || "Watch video"
      );
      setSevereLinkText(
        currentClinician?.recommendations?.severeLinkText || "Watch video"
      );
    }
  }, [currentClinician]);

  return (
    <Styled>
      <div className="description-section">
        <ul>
          <li>
            This section allows you to provide custom recommendations for
            athletes based on their movement assessment results. For each result
            category - Normal, Significant, and Severe - you can input a plan
            that your athletes will see as their &quot;Team Recommendation&quot;
            after completing their analysis.
          </li>
          <li>
            Recommendations can include links (e.g., to exercise videos or
            protocols) and optional notes or instructions (e.g., how often to
            complete the exercises). Athletes will be able to view this guidance
            directly from their report screen.
          </li>
        </ul>
      </div>
      <div className="recommendations-section">
        <div className="recommendation-level">
          <h3>Level 1 - Normal</h3>
          <textarea
            value={normal}
            disabled={loading}
            onChange={(e) => setNormal(e.target.value)}
            placeholder="Type recommendations here..."
          />
          <div className="link-input-container">
            <input
              type="url"
              value={normalLink}
              disabled={loading}
              onChange={handleUrlChange(setNormalLink)}
              placeholder="Eg: youtube.com"
            />
            <input
              type="text"
              value={normalLinkText}
              disabled={loading}
              onChange={(e) => setNormalLinkText(e.target.value)}
              placeholder="Eg:Watch video"
            />
          </div>
        </div>
        <div className="recommendation-level">
          <h3>Level 2 - Significant</h3>
          <textarea
            value={significant}
            disabled={loading}
            onChange={(e) => setSignificant(e.target.value)}
            placeholder="Type recommendations here..."
          />
          <div className="link-input-container">
            <input
              type="url"
              value={significantLink}
              disabled={loading}
              onChange={handleUrlChange(setSignificantLink)}
              placeholder="Eg: youtube.com"
            />
            <input
              type="text"
              value={significantLinkText}
              disabled={loading}
              onChange={(e) => setSignificantLinkText(e.target.value)}
              placeholder="Eg:Watch video"
            />
          </div>
        </div>
        <div className="recommendation-level">
          <h3>Level 3 - Severe</h3>
          <textarea
            value={severe}
            disabled={loading}
            onChange={(e) => setSevere(e.target.value)}
            placeholder="Type recommendations here..."
          />
          <div className="link-input-container">
            <input
              type="url"
              value={severeLink}
              disabled={loading}
              onChange={handleUrlChange(setSevereLink)}
              placeholder="Eg: youtube.com"
            />
            <input
              type="text"
              value={severeLinkText}
              disabled={loading}
              onChange={(e) => setSevereLinkText(e.target.value)}
              placeholder="Eg:Watch video"
            />
          </div>
        </div>
        <div className="save-button-container">
          <Button
            type="submit"
            color="primary"
            variant="contained"
            style={{
              ...btnStyle,
              width: "158px",
              color: "white",
              top: "0",
              textTransform: "none",
            }}
            onClick={(e) => {
              handleSave();
            }}
          >
            {loading ? (
              <CircularProgress color="inherit" size={20} />
            ) : (
              "Save Changes"
            )}
          </Button>
        </div>
      </div>
    </Styled>
  );
};

export default TeamRecommendations;
